
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card } from '@/components/ui/card';
import { useAdminStats } from '@/hooks/admin/useAdminStats';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import TimeRangeSelector from './TimeRangeSelector';

const UserStatsOverTimeChart: React.FC = () => {
  const { historicalStats, isLoadingHistorical, errorHistorical, timeRange, setTimeRange } = useAdminStats();
  
  if (isLoadingHistorical) {
    return <div className="flex justify-center items-center h-80">Loading historical data...</div>;
  }
  
  if (errorHistorical) {
    return <div className="flex justify-center items-center h-80 text-red-500">{errorHistorical}</div>;
  }
  
  // Format the date and calculate incremental values (differences between days)
  const formattedData = historicalStats.map((stat, index) => {
    const nzDate = toZonedTime(new Date(stat.date), 'Pacific/Auckland');
    
    // For the first day, use the actual values as the incremental values
    if (index === 0) {
      return {
        ...stat,
        date: format(nzDate, 'MMM d')
      };
    }
    
    // For subsequent days, calculate the difference from the previous day
    const prev = historicalStats[index - 1];
    return {
      date: format(nzDate, 'MMM d'),
      pending: stat.pending - prev.pending,
      monthly: stat.monthly - prev.monthly,
      annual: stat.annual - prev.annual,
      lifetime: stat.lifetime - prev.lifetime,
      canceled: stat.canceled - prev.canceled
    };
  });
  
  return (
    <Card className="p-4">
      <div className="flex justify-end mb-4">
        <TimeRangeSelector timeRange={timeRange} setTimeRange={setTimeRange} />
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart
          data={formattedData}
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Area type="monotone" dataKey="pending" stackId="1" stroke="#8884d8" fill="#8884d8" name="Pending" />
          <Area type="monotone" dataKey="monthly" stackId="1" stroke="#82ca9d" fill="#82ca9d" name="Monthly" />
          <Area type="monotone" dataKey="annual" stackId="1" stroke="#ffc658" fill="#ffc658" name="Annual" />
          <Area type="monotone" dataKey="lifetime" stackId="1" stroke="#ff8042" fill="#ff8042" name="Lifetime" />
          <Area type="monotone" dataKey="canceled" stackId="1" stroke="#ff6b6b" fill="#ff6b6b" name="Cancelled" />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default UserStatsOverTimeChart;
